<template>
    <div class="add-channel-sidebar-content">
        <v-row class="ma-0 mb-4 header-row" align="center" justify="space-between">
            <div class="header-row__main">
                <v-btn
                    v-if="showChannelCreationBackButton"
                    icon
                    @click="goBack"
                    class="mr-2 back-btn"
                >
                    <v-icon>mdi-arrow-left</v-icon>
                </v-btn>
                <h2 class="dashboard">
                    {{ stageTitle }}
                </h2>
            </div>
            <v-btn
                icon
                class="close-btn"
                aria-label="Закрыть"
                @click="$emit('close')"
            >
                <v-icon>mdi-close</v-icon>
            </v-btn>
        </v-row>
        <div v-if="isSlotContextInitializing" class="add-channel-sidebar-loader">
            <v-progress-circular indeterminate size="36" width="3" color="mainNew" />
        </div>
        <template v-else>
        <template v-if="showSlotBillingBanner">
            <div class="slot-freeze-banner mb-3" :class="{ 'slot-freeze-banner--paid': slotBillingBannerMode === 'paid' }">
                <div class="slot-freeze-banner__left">
                    <span class="slot-freeze-banner__text">
                        {{ slotBillingBannerText }}
                    </span>
                </div>
                <div class="slot-freeze-banner__actions">
                    <div
                        v-if="slotAutoRenewalEnabled"
                        class="slot-freeze-banner__auto-renewal"
                        @click="openTariffBilling"
                    >
                        <div class="slot-freeze-banner__auto-renewal-title">Автопродление</div>
                        <div
                            v-if="slotNextPaymentShortLabel"
                            class="slot-freeze-banner__auto-renewal-date"
                        >
                            След. списание {{ slotNextPaymentShortLabel }}
                        </div>
                    </div>
                    <v-btn
                        v-else
                        small
                        :color="slotBillingBannerMode === 'frozen' ? '#FD9C2B' : '#378EFF'"
                        class="slot-freeze-banner__btn white--text"
                        @click="openTariffBilling"
                        elevation="0"
                    >
                        Продлить
                    </v-btn>
                    <v-menu v-if="selectedSlot && !isTariffSlot(selectedSlot)" offset-y left>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn icon x-small class="slot-freeze-banner__menu-btn" v-bind="attrs" v-on="on">
                                <v-icon size="22" color="#AAB1B9">mdi-dots-vertical</v-icon>
                            </v-btn>
                        </template>
                        <v-list dense>
                            <v-list-item @click="onBannerCancelSubscription">
                                <v-list-item-title>{{ slotAutoRenewalMenuText }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </div>
            </div>
            <div v-if="slotBillingBannerMode === 'frozen'" class="warning-text-wrapper mb-3">
                <div class="d-flex align-center mb-1">
                    <img class="mr-1 mb-1" style="width: 16px; height: 16px;" src="@/assets/icons/error-status.svg" />
                    <strong class="description-text-wrapper__warning">Важно!</strong>
                </div>
                <p class="description-text-wrapper__text">
                    {{ slotBillingBannerWarningText }}
                </p>
            </div>
        </template>
        <!-- ID коннектора для суперпользователя при создании канала (только после создания, на этапе 4) -->
        <div v-if="isSuperUser && channelCreationStage === 4 && connector && connector.connectorId" class="connector-id-wrapper mb-4">
            <div class="connector-id-container">
                <span class="connector-id-label">ID:</span>
                <code class="connector-id-value">{{ connector.connectorId }}</code>
                <v-tooltip bottom :value="copyTooltip" :open-on-hover="false">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            icon
                            x-small
                            class="connector-id-copy-btn"
                            @click="copyConnectorId"
                            v-bind="attrs"
                        >
                            <v-icon small :color="copyTooltip ? '#4CAF50' : '#378EFF'">
                                {{ copyTooltip ? 'mdi-check' : 'mdi-content-copy' }}
                            </v-icon>
                        </v-btn>
                    </template>
                    <span class="white--text">Скопировано</span>
                </v-tooltip>
            </div>
        </div>
        
        <!-- Stage 1: Выбор канала / провайдера -->
        <div v-if="channelCreationStage === 1" class="stage-content">
            <template v-if="slotBillingBannerMode === 'frozen' && connectTab !== 'transfer'">
            </template>
            <template v-else>
            <div v-if="project?.billingEnabled && project?.isChannelsSlots && !isMaxBusinessSlot" class="channel-create-tabs mb-4">
                <div
                    class="channel-create-tabs__slider"
                    :style="{ transform: connectTab === 'transfer' ? 'translateX(100%)' : 'translateX(0%)' }"
                ></div>
                <button
                    type="button"
                    class="channel-create-tabs__item"
                    :class="{ 'channel-create-tabs__item--active': connectTab === 'new' }"
                    @click="connectTab = 'new'"
                >
                    Подключить новый
                </button>
                <button
                    type="button"
                    class="channel-create-tabs__item"
                    :class="{ 'channel-create-tabs__item--active': connectTab === 'transfer' }"
                    @click="connectTab = 'transfer'"
                >
                    Перенести существующий
                </button>
            </div>

            <template v-if="connectTab === 'new' || (!project?.billingEnabled || !project?.isChannelsSlots)">
                <!-- Шаг 1a: Выбор типа канала (если группа не задана) -->
                <template v-if="!activeGroup">
                    <div class="mb-2" style="font-size: 13px; color: #2A3B5199;">
                        Выберите канал
                    </div>
                    <div class="channel-type-list">
                        <div
                            v-for="ch in channelTypeOptions"
                            :key="ch.id"
                            class="channel-type-item"
                            :class="{ 'channel-type-item--selected': selectedGroupId === ch.id }"
                            @click="selectedGroupId = ch.id"
                        >
                            <v-avatar :color="ch.avatarColor" size="36">
                                <v-img v-if="ch.avatarImage" :src="ch.avatarImage" />
                                <span v-else class="white--text" style="font-size: 14px; font-weight: 600;">{{ ch.avatarSymbol }}</span>
                            </v-avatar>
                            <span class="channel-type-item__name">{{ ch.name }}</span>
                            <v-icon
                                :color="selectedGroupId === ch.id ? '#378EFF' : '#AAB1B9'"
                                size="20"
                                class="ml-auto"
                            >
                                {{ selectedGroupId === ch.id ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank' }}
                            </v-icon>
                        </div>
                    </div>
                    <div class="d-flex justify-end mt-4">
                        <v-btn @click="confirmChannelType" color="mainNew" class="white--text" :disabled="!selectedGroupId">
                            Далее
                        </v-btn>
                    </div>
                </template>

                <!-- Шаг 1b: Выбор провайдера (группа задана) -->
                <template v-else>
                    <div class="mb-2" style="font-size: 13px; color: #2A3B5199;">
                        Выберите провайдера
                    </div>
                    <CustomActivatorSelect
                        class=""
                        :items="channelsList"
                        :transition="false"
                        item-value="id"
                        v-model="selectedChannelId"
                        :multiple="false"
                    >
                        <template v-slot:activator="{ selected, _id }">
                            <BaseInputOutlined
                                label="Провайдер"
                                :hide-details="true"
                                :persistent-label="true"
                                class="select-input bot-select"
                                append-icon="mdi-menu-down"
                                :id="_id"
                                :value="selected ? selected.name : ''"
                                readonly
                            >
                            </BaseInputOutlined>
                        </template>
                    </CustomActivatorSelect>

                    <div class="d-flex justify-end mt-4">
                        <v-btn @click="next" color="mainNew" class="white--text" :disabled="!selectedChannel">
                            Далее
                        </v-btn>
                    </div>
                </template>
            </template>

            <template v-else>
                <div class="mb-2" style="font-size: 13px; color: #2A3B5199;">
                    Выберите существующий канал для переноса
                </div>
                <div v-if="!selectedSlotId" class="channel-transfer-placeholder">
                    Не удалось определить слот для переноса.
                </div>
                <template v-else>
                    <div v-if="isSlotsLoading" class="channel-transfer-placeholder">
                        Загрузка списка каналов...
                    </div>
                    <div v-else-if="transferCandidates.length" class="channel-type-list">
                        <div
                            v-for="connector in transferCandidates"
                            :key="connector.connectorId || connector.id"
                            class="channel-type-item"
                            :class="{
                                'channel-type-item--selected': selectedTransferConnectorId === (connector.connectorId || connector.id),
                                'channel-type-item--transfer': true,
                            }"
                            @click="selectedTransferConnectorId = connector.connectorId || connector.id"
                        >
                            <div
                                class="channel-transfer-item__section"
                                :class="{
                                    'channel-transfer-item__section--tariff': getTransferConnectorSlotType(connector) === 'tariff',
                                    'channel-transfer-item__section--additional': getTransferConnectorSlotType(connector) === 'additional',
                                }"
                            >
                                {{ getTransferConnectorSlotShortLabel(connector) }}
                            </div>
                            <v-avatar :color="getTransferConnectorAvatar(connector).color || '#9E9E9E'" size="36">
                                <v-img v-if="getTransferConnectorAvatar(connector).image" :src="getTransferConnectorAvatar(connector).image" />
                                <span v-else class="white--text" style="font-size: 14px; font-weight: 600;">
                                    {{ getTransferConnectorAvatar(connector).symbol || '?' }}
                                </span>
                            </v-avatar>
                            <div class="channel-transfer-item__text">
                                <div class="channel-transfer-item__head">
                                    <span class="channel-type-item__name">{{ getTransferConnectorName(connector) }}</span>
                                </div>
                                <span v-if="getTransferConnectorPhone(connector)" class="channel-transfer-item__sub">
                                    {{ getTransferConnectorPhone(connector) }}
                                </span>
                            </div>
                            <v-icon
                                :color="selectedTransferConnectorId === (connector.connectorId || connector.id) ? '#378EFF' : '#AAB1B9'"
                                size="20"
                                class="ml-auto"
                            >
                                {{ selectedTransferConnectorId === (connector.connectorId || connector.id) ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank' }}
                            </v-icon>
                        </div>
                    </div>
                    <div v-else class="channel-transfer-placeholder">
                        Нет доступных каналов для переноса.
                    </div>
                    <div
                        v-if="selectedTransferConnectorLabel"
                        class="channel-transfer-warning mt-4"
                    >
                        <div class="channel-transfer-warning__title">Что произойдет</div>
                        <div class="channel-transfer-warning__text">
                            {{ selectedTransferConnectorLabel }} перенесётся на выбранное подключение, а его прежнее место освободится, и вы сможете подключить туда другой мессенджер.
                        </div>
                    </div>
                    <div class="d-flex justify-end mt-4">
                        <v-btn
                            color="mainNew"
                            class="white--text"
                            :loading="isTransferSubmitting"
                            :disabled="!selectedTransferConnectorId || isTransferSubmitting"
                            @click="transferExistingChannel"
                        >
                            Перенести
                        </v-btn>
                    </div>
                </template>
            </template>
            </template>
        </div>

        <!-- Stage 2: Ввод токена MAX Business -->
        <div v-else-if="channelCreationStage === 2" class="stage-content">
            <div class="mb-2" style="font-size: 13px; color: #2A3B5199;">
                Введите токен MAX Business
            </div>
            <BaseInputOutlined
                label="Токен MAX Business"
                v-model="maxBusinessToken"
                :type="'password'"
                class="mb-4"
            />
            <div
                v-if="maxBusinessTokenError"
                class="description-text-wrapper__warning mb-4"
                style="font-size: 13px;"
            >
                {{ maxBusinessTokenError }}
            </div>
            <div class="d-flex justify-end">
                <v-btn
                    @click="confirmMaxBusinessToken"
                    color="mainNew"
                    class="white--text"
                    :disabled="!maxBusinessToken.trim()"
                >
                    Далее
                </v-btn>
            </div>
        </div>

        <!-- Stage 3: Создание коннектора -->
        <div v-else-if="channelCreationStage === 3" class="stage-content">
            <!-- Спиннер показывается глобально через overlay на странице проекта -->
            <h2 class="dashboard">
                Создание {{ channelTypeName }}...
            </h2>
        </div>

        <!-- Stage 4: Настройки канала -->
        <div v-else-if="channelCreationStage === 4" class="stage-content">
            <div v-if="connector">
                <!-- Для всех каналов показываем ChannelSettingsForm -->
                <ChannelSettingsForm
                    v-if="providerType"
                    :value="settings"
                    :connector="connector"
                    :providerType="providerType"
                    :appCreated="appCreated"
                    :mode="'create'"
                    :inSidebar="true"
                    @input="settings = $event"
                    @saved="goToProject"
                    @authorized="onChannelAuthorizedDuringCreation"
                    @auth-status-changed="handleChannelAuthStatusChanged"
                />
            </div>
            <div v-else>
                <h2 class="dashboard">
                    Ошибка: коннектор не найден
                </h2>
            </div>
        </div>
        </template>
        <DetachChannelModal
            :opened="isDetachChannelModalOpened"
            :channel-name="detachChannelModalChannelName"
            :loading="detachChannelModalLoading"
            @cancel="closeDetachChannelModal"
            @create-package="handleDetachChannelCreatePackage"
        />
    </div>
</template>

<script>
import { mapState } from 'vuex'
import CustomActivatorSelect from '@/components/inputs/CustomActivatorSelect.vue'
import BaseInputOutlined from '@/components/inputs/BaseInputOutlined.vue'
import settingsList from '@/components/connectorSettings/list'
import { PROVIDER_TYPES, CHANNEL_TYPES, PROVIDER_TO_CHANNEL_MAP, CONNECTOR_TYPES } from '@/vars/general'
import { createChannel, getCascadesByProject, updateCascadeChannelPriority } from '@/api/networkFixer'
import * as networkFixerApi from '@/api/networkFixer'
import { fetchConnector } from '@/api/projects'
import cfg from '@root/config.js'
import whatsappLogo from '@/assets/img/messenger-logo/wa-logo.png'
import telegramLogo from '@/assets/img/messenger-logo/telegram-logo.png'
import maxLogo from '@/assets/img/messenger-logo/max-logo.png'
import smsLogo from '@/assets/img/messenger-logo/sms-logo.png'
import { useBillingStore } from '@/store/stores/billing'
import { getProviderOptionsByGroup, getProjectChannelGroups } from '@/helpers/channels/channelCatalog'
import DetachChannelModal from '@/modules/marketplace/components/billing/DetachChannelModal.vue'
import { toggleSubscription } from '@/api/marketplace'
import {
    applyCancelSubscriptionQueryParams,
    getConnectorChannelDisplayName,
} from '@/helpers/channels/getConnectorChannelDisplayName'
import { findFreeSmsPromoSlotId, getFirstFreeMaxSlotId, getFirstFreeSlotId } from '@/helpers/channels/channelSlots'
import { ensureProjectBillingLoaded } from '@/helpers/channels/ensureProjectBillingLoaded'

export default {
    name: 'AddChannelSidebarContent',
    components: {
        CustomActivatorSelect,
        BaseInputOutlined,
        ChannelSettingsForm: () => import('@/components/channels/ChannelSettingsForm.vue'),
        DetachChannelModal,
    },
    data: () => ({
        selectedChannelId: null,
        selectedGroupId: null,
        selectedTransferConnectorId: null,
        connectTab: 'new',
        isCreating: false,
        isLoading: false,
        isTransferSubmitting: false,
        isSlotsLoading: false,
        isCreationDataReady: false,
        settings: {},
        appCreated: true,
        channelSlots: [],
        currentGroup: null,
        urlCheckInterval: null,
        waitPrioritiesResolver: null,
        copyTooltip: false,
        maxBusinessToken: '',
        maxBusinessTokenError: '',
        isDetachChannelModalOpened: false,
        detachChannelModalLoading: false,
        detachChannelModalChannelName: 'канал',
        detachChannelModalSubscriptionId: null,
        channelWasAuthorizedDuringCreation: false,
    }),
    computed: {
        ...mapState('project', ['loggedAddingConnector', 'currentAddingConnector']),
        channelCreationStage() {
            return this.$store.state.channelCreationStage || 1
        },
        channelCreationMode() {
            return this.$store.state.channelCreationMode
        },
        isConnectorSettingsSidebar() {
            return this.$store.state.isConnectorSettingsSidebar
        },
        project() {
            return this.$store.getters.projectById(this.$router.currentRoute.params.id)
        },
        activeGroup() {
            const urlParams = new URLSearchParams(window.location.search)
            const groupFromUrl = urlParams.get('group')
            const groupFromRoute = this.$route?.query?.group
            return this.currentGroup || groupFromRoute || groupFromUrl || null
        },
        initialConnectTab() {
            const urlParams = new URLSearchParams(window.location.search)
            const tabFromUrl = urlParams.get('connectTab')
            const tabFromRoute = this.$route?.query?.connectTab
            // Важно: query в роуте может быть stale из-за history.replaceState,
            // поэтому приоритет у значения из URL.
            const tab = tabFromUrl || tabFromRoute
            return tab === 'transfer' ? 'transfer' : 'new'
        },
        selectedSlotId() {
            const urlParams = new URLSearchParams(window.location.search)
            const slotFromUrl = urlParams.get('slotId')
            const slotFromRoute = this.$route?.query?.slotId
            // route.query может быть stale при history.replaceState,
            // поэтому slotId берем в приоритете из URL.
            const raw = slotFromUrl || slotFromRoute
            if (raw === null || raw === undefined || raw === '') return null
            return String(raw)
        },
        channelTypeOptions() {
            const options = [
                { id: 'whatsapp', name: 'WhatsApp', avatarColor: '#29a71a', avatarImage: whatsappLogo, avatarSymbol: null },
                { id: 'telegram', name: 'Telegram', avatarColor: '#24aaeb', avatarImage: telegramLogo, avatarSymbol: null },
                { id: 'max', name: 'MAX', avatarColor: '#3b47ec', avatarImage: maxLogo, avatarSymbol: null },
                { id: 'maxBusiness', name: 'MAX Business', avatarColor: '#3b47ec', avatarImage: maxLogo, avatarSymbol: null },
                { id: 'sms', name: 'SMS', avatarColor: '#43A047', avatarImage: smsLogo, avatarSymbol: null },
            ]
            if (this.isMaxBusinessSlot) {
                return options.filter(option => option.id === 'maxBusiness')
            }
            if (this.selectedSlot) {
                return options.filter(option => option.id !== 'maxBusiness')
            }
            return options
        },
        channelsList() {
            const groupFromQuery = this.activeGroup
            
            const allGroups = {
                whatsapp: getProviderOptionsByGroup('whatsapp'),
                telegram: getProviderOptionsByGroup('telegram'),
                max: getProviderOptionsByGroup('max'),
                maxBusiness: getProviderOptionsByGroup('maxBusiness'),
                sms: getProviderOptionsByGroup('sms'),
            }

            // Если группа указана в query, показываем только типы этой группы
            if (groupFromQuery && allGroups[groupFromQuery]) {
                return allGroups[groupFromQuery]
            }

            // Если мы в режиме создания канала, но группа еще не установлена, возвращаем пустой список
            // (группа должна быть установлена при открытии сайдбара)
            if (this.channelCreationMode) {
                return []
            }

            // Иначе показываем все типы всех групп (для обратной совместимости, когда не в режиме создания)
            return [
                ...allGroups.whatsapp,
                ...allGroups.telegram,
                ...allGroups.max,
                ...allGroups.maxBusiness,
                ...allGroups.sms,
            ]
        },
        selectedChannel() {
            if (!this.selectedChannelId) return
            return this.channelsList.find(el => el.id === this.selectedChannelId)
        },
        selectedSlot() {
            if (this.selectedSlotId === null) return null
            return (this.channelSlots || []).find(slot => String(slot.id) === this.selectedSlotId) || null
        },
        isMaxBusinessSlot() {
            return this.selectedSlot?.type === 'maxSlot'
        },
        slotPayUntilLabel() {
            const raw = this.selectedSlot?.channelDeletedDate
            if (!raw) return ''
            const dt = new Date(raw)
            if (Number.isNaN(dt.getTime())) return ''
            dt.setDate(dt.getDate() - 1)
            return dt.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
        },
        slotPaidUntilLabel() {
            const raw = this.selectedSlot?.nextPayment
            if (!raw) return ''
            const dt = new Date(raw)
            if (Number.isNaN(dt.getTime())) return ''
            return dt.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
        },
        slotNextPaymentShortLabel() {
            const raw = this.selectedSlot?.nextPayment
            if (!raw) return ''
            const dt = new Date(raw)
            if (Number.isNaN(dt.getTime())) return ''
            return dt.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' })
        },
        slotAutoRenewalEnabled() {
            return this.selectedSlot?.isAutoRenewalEnabled === true
        },
        slotAutoRenewalMenuText() {
            return this.slotAutoRenewalEnabled ? 'Выключить автопродление' : 'Включить автопродление'
        },
        isNoTariffBillingState() {
            const projectId = this.project?.id
            if (!projectId) return false
            const billingStore = useBillingStore()
            return !!this.project?.billingEnabled && !billingStore.getCurrentTariffState(projectId)
        },
        tariffEndDateLabel() {
            const projectId = this.project?.id
            if (!projectId) return ''
            const billingStore = useBillingStore()
            const raw = billingStore.getBlockedTariffPayUntil(projectId)
            if (!raw) return ''
            const dt = new Date(raw)
            if (Number.isNaN(dt.getTime())) return ''
            return dt.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
        },
        slotBillingBannerMode() {
            if (this.isSlotsLoading) {
                return null
            }
            if (this.isNoTariffBillingState) return 'frozen'
            if (this.slotPayUntilLabel) return 'frozen'
            if (this.slotPaidUntilLabel) return 'paid'
            return null
        },
        showSlotBillingBanner() {
            // Скрываем плашку только на этапе "Настройка",
            // когда уже есть connectorId и отображается статус в настройках канала.
            if (this.channelCreationStage === 4) return false
            return !!this.slotBillingBannerMode
        },
        slotBillingBannerDate() {
            if (this.slotBillingBannerMode === 'frozen') return this.slotPayUntilLabel
            if (this.slotBillingBannerMode === 'paid') return this.slotPaidUntilLabel
            return ''
        },
        slotBillingBannerText() {
            if (this.isNoTariffBillingState && this.tariffEndDateLabel) return `Оплатить до: ${this.tariffEndDateLabel}`
            if (this.isNoTariffBillingState) return 'Оплатите тариф'
            if (this.slotBillingBannerMode === 'frozen' && this.slotBillingBannerDate) return `Оплатить до: ${this.slotBillingBannerDate}`
            if (this.slotBillingBannerMode === 'frozen') return 'Оплатите канал или тариф'
            if (this.isTariffSlot(this.selectedSlot)) {
                // На узких экранах убираем «Тариф оплачен», чтобы дата не съедалась ellipsis.
                return this.isNarrowBannerText
                    ? `До: ${this.slotBillingBannerDate}`
                    : `Тариф оплачен до: ${this.slotBillingBannerDate}`
            }
            return `Оплачен до: ${this.slotBillingBannerDate}`
        },
        isNarrowBannerText() {
            return (this.$vuetify?.breakpoint?.width || 0) < 480
        },
        slotBillingBannerWarningText() {
            if (this.isNoTariffBillingState) {
                return 'Тариф не оплачен и все каналы заморожены. Продлите подписку, чтобы возобновить работу.'
            }
            return 'Возможность подключения мессенджера на этот свободный канал приостановлена. Оплатите подписку на канал, чтобы подключить мессенджер.'
        },
        selectedTransferConnectorLabel() {
            if (!this.selectedTransferConnectorId) return ''
            const selected = this.transferCandidates.find(c =>
                (c.connectorId || c.id) === this.selectedTransferConnectorId
            )
            if (!selected) return ''
            return this.getTransferConnectorName(selected)
        },
        transferCandidates() {
            if (!this.selectedSlot || !this.project?.connectors) return []
            const isTargetTariff = this.isTariffSlot(this.selectedSlot)
            const connectors = this.project.connectors || []
            const connectorById = {}
            connectors.forEach(connector => {
                const key = connector.connectorId || connector.id
                if (key) connectorById[key] = connector
            })

            // Берем кандидатов именно из слотов, чтобы четко разделять тариф/доп секции.
            // Если целевой слот тарифный -> только из доп. секции.
            // Если целевой слот дополнительный -> из обеих секций.
            const allowedSlots = (this.channelSlots || []).filter(slot => {
                if (!slot?.channelConnectorId) return false
                return isTargetTariff ? !this.isTariffSlot(slot) : true
            })

            const unique = {}
            allowedSlots.forEach(slot => {
                const connector = connectorById[slot.channelConnectorId]
                if (connector) {
                    const key = connector.connectorId || connector.id
                    unique[key] = connector
                }
            })

            return Object.values(unique).filter(connector => {
                const groupKey = this.getTransferChannelGroupKey(connector)
                if (this.isMaxBusinessSlot) {
                    return groupKey === CHANNEL_TYPES.maxBusiness
                }
                return groupKey !== CHANNEL_TYPES.maxBusiness
            })
        },
        channelTypeName() {
            const group = getProjectChannelGroups().find(item => item.name === this.activeGroup)
            return group?.displayName || 'канал'
        },
        stageTitle() {
            const channelName = this.channelTypeName
            if (this.channelCreationStage === 1) {
                return `Добавить ${channelName}`
            } else if (this.channelCreationStage === 2) {
                if (this.providerType === PROVIDER_TYPES.maxBusiness) {
                    return 'Токен MAX Business'
                }
                return `Создание ${channelName}`
            } else if (this.channelCreationStage === 3) {
                return `Создание ${channelName}`
            } else if (this.channelCreationStage === 4) {
                return `Настройка ${channelName}`
            }
            return `Добавить ${channelName}`
        },
        connector() {
            // Получаем коннектор из store
            const loggedConnector = this.loggedAddingConnector
            if (loggedConnector) {
                return loggedConnector
            }
            return null
        },
        providerType() {
            // Получаем providerType из connector или из currentAddingConnector
            if (this.connector?.providerType) {
                return this.connector.providerType
            }
            if (this.currentAddingConnector?.providerType) {
                return this.currentAddingConnector.providerType
            }
            return null
        },
        isChannelCreationLocked() {
            return (this.$store.state.channelCreationLockCount || 0) > 0
        },
        isSuperUser() {
            return cfg.adminsWhitelist.includes(this.$auth.user.sub)
        },
        isStarterPackChannelCreation() {
            return this.$store.state.channelCreationContext === 'starter-pack'
        },
        isCustomCascadesChannelCreation() {
            return this.$store.state.channelCreationContext === 'custom-cascades'
        },
        isSlotContextInitializing() {
            return !this.isCreationDataReady
        },
        showChannelCreationBackButton() {
            if (this.isSlotContextInitializing || this.isMaxBusinessSlot) return false
            const canGoBack =
                this.channelCreationStage > 1 ||
                (this.channelCreationStage === 1 && this.activeGroup)
            if (!canGoBack) return false
            if (this.channelCreationStage === 4 && this.channelWasAuthorizedDuringCreation) {
                return false
            }
            return true
        },
    },
    watch: {
        channelCreationStage(newStage) {
            if (newStage === 3) {
                this.createAndAddChannel()
            }
        },
        // Следим за изменением query параметра group и сбрасываем состояние
        '$route.query.group'(newGroup, oldGroup) {
            // Если изменился group, сбрасываем выбранный канал и этап
            if (newGroup !== oldGroup) {
                this.resetChannelSelection()
            }
        },
        // Следим за открытием/закрытием сайдбара
        channelCreationMode(newValue) {
            // При открытии сайдбара обновляем currentGroup из URL
            if (newValue) {
                const urlParams = new URLSearchParams(window.location.search)
                this.currentGroup = urlParams.get('group')
                this.connectTab = this.initialConnectTab
                this.resetChannelSelection()
                void this.prepareChannelCreationData()
            } else {
                this.selectedChannelId = null
                this.selectedGroupId = null
                this.selectedTransferConnectorId = null
                this.connectTab = 'new'
                this.currentGroup = null
                this.channelWasAuthorizedDuringCreation = false
            }
        },
        selectedSlotId() {
            this.sanitizeChannelGroupForSlot()
        },
        channelSlots() {
            this.sanitizeChannelGroupForSlot()
        },
        // Следим за изменениями URL через polling (так как history API не триггерит реактивность)
        isConnectorSettingsSidebar(newValue) {
            if (newValue && this.channelCreationMode) {
                // При открытии сайдбара проверяем URL и обновляем currentGroup
                const urlParams = new URLSearchParams(window.location.search)
                const newGroup = urlParams.get('group')
                if (newGroup !== this.currentGroup) {
                    this.currentGroup = newGroup
                    this.resetChannelSelection()
                }
                // Также периодически проверяем изменения URL (на случай использования window.history.replaceState)
                this.urlCheckInterval = setInterval(() => {
                    const urlParams = new URLSearchParams(window.location.search)
                    const currentGroupFromUrl = urlParams.get('group')
                    if (currentGroupFromUrl !== this.currentGroup) {
                        this.currentGroup = currentGroupFromUrl
                    }
                }, 100)
            } else {
                // Очищаем интервал при закрытии сайдбара
                if (this.urlCheckInterval) {
                    clearInterval(this.urlCheckInterval)
                    this.urlCheckInterval = null
                }
            }
        },
        // Следим за изменением списка провайдеров и автоматически выбираем провайдера
        // (единственный вариант в группе либо наш провайдер по умолчанию — whatsappDh / maxDh),
        // чтобы пропустить шаг ручного выбора провайдера.
        channelsList() {
            this.autoSelectProviderIfPossible()
        },
        // Также следим за изменением currentGroup для автоматического выбора
        currentGroup(newGroup, oldGroup) {
            if (newGroup !== oldGroup && this.channelCreationStage === 1) {
                this.$nextTick(() => {
                    this.autoSelectProviderIfPossible()
                })
            }
        },
    },
    methods: {
        async prepareChannelCreationData() {
            this.isCreationDataReady = false
            try {
                const projectId = this.project?.id
                await Promise.all([
                    ensureProjectBillingLoaded(projectId, this.project),
                    this.loadSlots(),
                ])
                this.sanitizeChannelGroupForSlot()
                this.autoSelectProviderIfPossible()
                await this.$nextTick()
            } finally {
                this.isCreationDataReady = true
            }
        },
        sanitizeChannelGroupForSlot() {
            if (!this.selectedSlot) return

            if (this.isMaxBusinessSlot) {
                if (this.connectTab === 'transfer') {
                    this.connectTab = 'new'
                }
                if (this.activeGroup !== 'maxBusiness') {
                    this.currentGroup = 'maxBusiness'
                    this.selectedGroupId = 'maxBusiness'
                    const newUrl = new URL(window.location.href)
                    newUrl.searchParams.set('group', 'maxBusiness')
                    window.history.replaceState({}, '', newUrl.toString())
                }
                return
            }

            const shouldClearGroup =
                this.activeGroup === 'maxBusiness' ||
                this.currentGroup === 'maxBusiness' ||
                this.selectedGroupId === 'maxBusiness'

            if (shouldClearGroup) {
                this.currentGroup = null
                this.selectedGroupId = null
                const newUrl = new URL(window.location.href)
                if (newUrl.searchParams.get('group') === 'maxBusiness') {
                    newUrl.searchParams.delete('group')
                    window.history.replaceState({}, '', newUrl.toString())
                }
            }

            if (!this.selectedTransferConnectorId) return
            const connector = (this.project?.connectors || []).find(
                item => (item.connectorId || item.id) === this.selectedTransferConnectorId
            )
            if (connector && this.getTransferChannelGroupKey(connector) === CHANNEL_TYPES.maxBusiness) {
                this.selectedTransferConnectorId = null
            }
        },
        handleChannelAuthStatusChanged(payload) {
            this.$root.$emit('reload-channel-auth-status', payload)
        },
        onBannerChangeChannel() {
            this.$store.commit('setChannelCreationMode', true)
            this.$store.commit('setChannelCreationStage', 1)
            this.connectTab = 'transfer'

            const newUrl = new URL(window.location.href)
            newUrl.searchParams.set('connectTab', 'transfer')
            if (this.selectedSlotId !== null && this.selectedSlotId !== undefined) {
                newUrl.searchParams.set('slotId', String(this.selectedSlotId))
            } else {
                newUrl.searchParams.delete('slotId')
            }
            window.history.replaceState({}, '', newUrl.toString())
        },
        async onBannerCancelSubscription() {
            if (!this.slotAutoRenewalEnabled) {
                await this.enableSlotAutoRenewal()
                return
            }
            const connectorId = this.selectedSlot?.channelConnectorId
            const slotId = this.selectedSlotId
            if (!connectorId && !slotId) {
                this.$store.dispatch('callNotify', 'Не удалось определить слот для отмены подписки')
                return
            }
            this.$store.commit('setChannelCreationMode', false)
            this.$store.commit('setChannelCreationStage', null)
            const billingStore = useBillingStore()
            applyCancelSubscriptionQueryParams({
                renewalMode: this.selectedSlot?.renewalMode,
                channelName: this.getSelectedSlotChannelDisplayName(),
                periodTo:
                    this.selectedSlot?.nextPayment ||
                    billingStore.getTariffPeriodEnd(this.project?.id) ||
                    null,
            })
            this.$store.commit(
                'setConnectorSettingsSidebarId',
                connectorId ? `cancel-subscription:${connectorId}` : `cancel-subscription:slot:${slotId}`
            )
            this.$store.commit('setIsConnectorSettingsSidebar', true)
        },
        getSelectedSlotChannelDisplayName() {
            const slot = this.selectedSlot
            if (!slot?.channelConnectorId) return ''
            const connector = (this.project?.connectors || []).find(
                (item) => (item.connectorId || item.id) === slot.channelConnectorId
            )
            return getConnectorChannelDisplayName(connector, this.project?.connectors || [])
        },
        async enableSlotAutoRenewal() {
            const projectId = this.project?.id
            const subscriptionId = this.selectedSlot?.subscriptionId
            if (!projectId || !subscriptionId) {
                this.$store.dispatch('callNotify', 'Не удалось определить подписку')
                return
            }
            const { error } = await toggleSubscription({
                projectId,
                subscriptionId,
                status: 'enable',
            })
            if (error) {
                this.$store.dispatch('callNotify', 'Не удалось включить автопродление')
                return
            }
            await this.loadSlots()
            this.$store.dispatch('callNotify', 'Автопродление включено')
        },
        closeDetachChannelModal() {
            this.isDetachChannelModalOpened = false
            this.detachChannelModalSubscriptionId = null
            this.detachChannelModalLoading = false
        },
        async handleDetachChannelCreatePackage() {
            const subscriptionId = this.detachChannelModalSubscriptionId
            if (!subscriptionId) {
                this.$store.dispatch('callNotify', 'Не удалось определить канал для отвязки')
                return
            }
            this.detachChannelModalLoading = true
            const { error } = await networkFixerApi.unchainSlot({ subscriptionId })
            if (error) {
                this.detachChannelModalLoading = false
                this.$store.dispatch('callNotify', 'Ошибка при отвязке дополнительного канала')
                return
            }
            this.detachChannelModalLoading = false
            this.closeDetachChannelModal()
            await this.loadSlots()
            this.$store.dispatch('callNotify', 'Канал переведен в отдельный пакет')
        },
        async openTariffBilling() {
            const projectId = this.project?.id
            if (!projectId) {
                this.$store.dispatch('callNotify', 'Проект не найден')
                return
            }
            if (this.isNoTariffBillingState) {
                this.$router.push(`/project/${this.project.id}/billing/?section=tariffs&highlighted=true`)
                return
            }
            const billingStore = useBillingStore()
            await billingStore.fetchCurrentTariff(projectId)
            const current = billingStore.getCurrentTariff(projectId)
            if (!current?.integrationId) {
                this.$store.dispatch('callNotify', 'Не удалось получить текущий тариф')
                return
            }
            this.$store.commit('setChannelCreationMode', false)
            this.$store.commit('setChannelCreationStage', null)
            this.$store.commit('setIsConnectorSettingsSidebar', false)
            this.$store.commit('setConnectorSettingsSidebarId', null)
            billingStore.selectedTariffId = current.integrationId
            billingStore.selectedSubscriptionPeriod = String(current.periodInMonths || '12')
            if (this.isTariffSlot(this.selectedSlot) || !this.selectedSlotId || this.selectedSlot?.renewalMode === 'with_tariff') {
                billingStore.setAddChannelContext(null)
            } else {
                const isEmptySlot = !this.selectedSlot?.channelConnectorId
                const channelMetaMap = {
                    whatsapp: {
                        name: 'WhatsApp',
                        kind: 'whatsapp',
                        icon: require('@/assets/icons/whatsApp-white.svg'),
                    },
                    telegram: {
                        name: 'Telegram',
                        kind: 'telegram',
                        icon: require('@/assets/icons/telegram-white.svg'),
                    },
                    max: {
                        name: 'MAX',
                        kind: 'max',
                        icon: require('@/assets/icons/max-white.svg'),
                    },
                }
                const baseMeta = isEmptySlot
                    ? {
                          name: 'WA/TG/MAX/SMS',
                          icon: null,
                          kind: 'bundle',
                      }
                    : (channelMetaMap[this.activeGroup] || {
                          name: 'WA/TG/MAX/SMS',
                          icon: null,
                          kind: 'bundle',
                      })
                billingStore.setAddChannelContext({
                    ...baseMeta,
                    subscriptionId:
                        typeof this.selectedSlot?.subscriptionId === 'number'
                            ? this.selectedSlot.subscriptionId
                            : null,
                    renewalMode: this.selectedSlot?.renewalMode ?? null,
                })
            }
            billingStore.setPaymentModalOpened(true, true)
        },
        isTariffSlot(slot) {
            const value = slot?.isTariff
            return value === true || value === 1 || value === '1' || value === 'true'
        },
        async loadSlots() {
            if (!this.project?.id) {
                this.isSlotsLoading = false
                return
            }
            this.isSlotsLoading = true
            try {
                const { data, error } = await networkFixerApi.getSlots({ projectId: this.project.id })
                if (!error) {
                    this.channelSlots = Array.isArray(data) ? data : []
                } else {
                    this.channelSlots = []
                }
            } catch (e) {
                this.channelSlots = []
            } finally {
                this.isSlotsLoading = false
            }
        },
        isCreatingMaxBusinessChannel() {
            const providerType = this.currentAddingConnector?.providerType || this.currentAddingConnector?.id
            const channelType = this.currentAddingConnector?.channelType
            return (
                providerType === PROVIDER_TYPES.maxBusiness ||
                channelType === CHANNEL_TYPES.maxBusiness ||
                this.activeGroup === 'maxBusiness'
            )
        },
        async resolveSlotIdForChannelAssignment() {
            const wantMaxSlot = this.isCreatingMaxBusinessChannel()
            if (!wantMaxSlot && this.$store.state.channelCreationContext === 'sms-promo') {
                await this.loadSlots()
                return findFreeSmsPromoSlotId(this.channelSlots)
            }
            const selectedMatchesChannel = this.selectedSlotId !== null && this.isMaxBusinessSlot === wantMaxSlot

            if (selectedMatchesChannel) {
                return this.selectedSlotId
            }

            await this.loadSlots()
            return wantMaxSlot
                ? getFirstFreeMaxSlotId(this.channelSlots)
                : getFirstFreeSlotId(this.channelSlots)
        },
        getTransferConnectorAvatar(connector) {
            const avatarType = connector?.connectorType || connector?.providerType
            const byType = this.$store.getters?.connectorAvatarByType
            if (typeof byType === 'function') {
                return byType(avatarType) || { color: '#9E9E9E', symbol: '?', image: null, name: '' }
            }
            return { color: '#9E9E9E', symbol: '?', image: null, name: '' }
        },
        getTransferConnectorPhone(connector) {
            return connector?.phone || ''
        },
        getTransferConnectorSlotType(connector) {
            const connectorId = connector?.connectorId || connector?.id
            if (!connectorId) return null
            const slot = (this.channelSlots || []).find(s => s?.channelConnectorId === connectorId)
            if (!slot) return null
            return this.isTariffSlot(slot) ? 'tariff' : 'additional'
        },
        getTransferConnectorSlotLabel(connector) {
            const type = this.getTransferConnectorSlotType(connector)
            if (type === 'tariff') return 'Тарифный'
            if (type === 'additional') return 'Дополнительный'
            return ''
        },
        getTransferConnectorSlotShortLabel(connector) {
            const type = this.getTransferConnectorSlotType(connector)
            if (type === 'tariff') return 'тариф'
            if (type === 'additional') return 'доп.'
            return ''
        },
        getTransferChannelGroupKey(connector) {
            const type = connector?.connectorType
            if (type === CHANNEL_TYPES.whatsappChannel) {
                return CHANNEL_TYPES.whatsappChannel
            }
            if (type === CHANNEL_TYPES.telegramChannel) {
                return CHANNEL_TYPES.telegramChannel
            }
            if (type === CHANNEL_TYPES.maxChannel) {
                return CHANNEL_TYPES.maxChannel
            }
            if (type === CHANNEL_TYPES.maxBusiness) {
                return CHANNEL_TYPES.maxBusiness
            }
            if (type === CHANNEL_TYPES.smsChannel) {
                return CHANNEL_TYPES.smsChannel
            }
            return type || ''
        },
        getTransferChannelBaseName(connector) {
            const type = connector?.connectorType
            const baseNames = {
                [CHANNEL_TYPES.whatsappChannel]: 'WhatsApp',
                [CHANNEL_TYPES.telegramChannel]: 'Telegram',
                [CHANNEL_TYPES.maxChannel]: 'MAX',
                [CHANNEL_TYPES.maxBusiness]: 'MAX Business',
                [CHANNEL_TYPES.smsChannel]: 'SMS',
            }
            return baseNames[type] || 'Канал'
        },
        getTransferChannelIndex(connector) {
            const connectors = (this.project?.connectors || [])
                .filter(c => !c?.isChisai)
                .filter(c => this.getTransferChannelGroupKey(c) === this.getTransferChannelGroupKey(connector))
                .sort((a, b) => {
                    // Повторяем логику сортировки из ChannelsTable для идентичной индексации.
                    const aTime = a?.createdAt && a.createdAt > 0 ? a.createdAt : Number.MAX_SAFE_INTEGER
                    const bTime = b?.createdAt && b.createdAt > 0 ? b.createdAt : Number.MAX_SAFE_INTEGER
                    if (aTime !== Number.MAX_SAFE_INTEGER && bTime !== Number.MAX_SAFE_INTEGER) return aTime - bTime
                    if (aTime === Number.MAX_SAFE_INTEGER && bTime !== Number.MAX_SAFE_INTEGER) return 1
                    if (aTime !== Number.MAX_SAFE_INTEGER && bTime === Number.MAX_SAFE_INTEGER) return -1
                    return String(a?.connectorId || '').localeCompare(String(b?.connectorId || ''))
                })
            const idx = connectors.findIndex(c => (c?.connectorId || c?.id) === (connector?.connectorId || connector?.id))
            return idx >= 0 ? idx + 1 : 1
        },
        getTransferConnectorName(connector) {
            const baseName = this.getTransferChannelBaseName(connector)
            const index = this.getTransferChannelIndex(connector)
            return index === 1 ? baseName : `${baseName} ${index}`
        },
        async transferExistingChannel() {
            if (this.selectedSlotId === null || !this.selectedTransferConnectorId) return
            const connector = (this.project?.connectors || []).find(
                item => (item.connectorId || item.id) === this.selectedTransferConnectorId
            )
            const groupKey = this.getTransferChannelGroupKey(connector)
            if (this.isMaxBusinessSlot && groupKey !== CHANNEL_TYPES.maxBusiness) {
                this.$store.dispatch('callNotify', 'В слот MAX Business можно перенести только канал MAX Business')
                return
            }
            if (!this.isMaxBusinessSlot && groupKey === CHANNEL_TYPES.maxBusiness) {
                this.$store.dispatch('callNotify', 'Канал MAX Business нельзя перенести в обычный слот')
                return
            }
            this.isTransferSubmitting = true
            try {
                const { error } = await networkFixerApi.setSlot({
                    slotId: this.selectedSlotId,
                    channelConnectorId: this.selectedTransferConnectorId,
                })
                if (error) {
                    this.$store.dispatch('callNotify', 'Не удалось перенести канал в слот')
                    return
                }
                this.$root.$emit('reload-channel-slots')
                this.$store.dispatch('callNotify', 'Канал успешно перенесен')
                this.goToProject()
            } catch (e) {
                this.$store.dispatch('callNotify', 'Не удалось перенести канал в слот')
            } finally {
                this.isTransferSubmitting = false
            }
        },
        confirmChannelType() {
            if (!this.selectedGroupId) return
            this.currentGroup = this.selectedGroupId
            const newUrl = new URL(window.location.href)
            newUrl.searchParams.set('group', this.selectedGroupId)
            window.history.replaceState({}, '', newUrl.toString())
            this.selectedChannelId = null
        },
        resetChannelSelection() {
            if (this.isChannelCreationLocked) {
                this.$store.dispatch('callNotify', 'Дождитесь завершения создания канала и установки приоритетов')
                return
            }
            this.selectedChannelId = null
            this.selectedGroupId = null
            this.selectedTransferConnectorId = null
            this.connectTab = this.initialConnectTab
            this.$store.commit('setChannelCreationStage', 1)
            this.$store.commit('project/updateCurrentAddingConnector', null)
            const urlParams = new URLSearchParams(window.location.search)
            const groupFromUrl = urlParams.get('group')
            if (groupFromUrl !== this.currentGroup) {
                this.currentGroup = groupFromUrl
            }
        },
        // Провайдер по умолчанию для группы, чтобы пропускать шаг ручного выбора.
        // В группах с несколькими провайдерами (whatsapp, max) сразу берём наш —
        // сменить на Провайдер 1/2 можно в настройках канала внутри сайдбара (фоллбэк).
        defaultProviderForGroup(group) {
            const map = {
                whatsapp: PROVIDER_TYPES.whatsappDh,
                max: PROVIDER_TYPES.maxDh,
            }
            return map[group] || null
        },
        // Авто-выбор провайдера на первом этапе: если в группе один вариант — берём его,
        // если несколько — берём наш дефолтный и сразу идём дальше, без шага «Выберите провайдера».
        autoSelectProviderIfPossible() {
            if (this.channelCreationStage !== 1 || this.connectTab !== 'new') return
            if (this.selectedChannelId) return
            const list = this.channelsList
            if (!list || !list.length) return

            let targetId = null
            if (list.length === 1) {
                targetId = list[0].id
            } else {
                const preferred = this.defaultProviderForGroup(this.activeGroup)
                if (preferred && list.some(el => el.id === preferred)) {
                    targetId = preferred
                }
            }
            if (!targetId) return

            this.selectedChannelId = targetId
            this.$nextTick(() => {
                this.next()
            })
        },
        next() {
            if (this.isChannelCreationLocked) {
                this.$store.dispatch('callNotify', 'Дождитесь завершения создания канала и установки приоритетов')
                return
            }
            if (!this.selectedChannel) return
            const providerType = this.selectedChannel.id
            
            // Сохраняем информацию о выборе, включая флаг для WhatsApp (рассылки)
            // Сохраняем информацию о выборе
            const channelData = {
                ...this.selectedChannel,
                providerType,
            }
            
            // Для всех провайдеров WhatsApp создаем как канал
            channelData.channelType = PROVIDER_TO_CHANNEL_MAP[providerType]
            
            this.$store.commit('project/updateCurrentAddingConnector', channelData)
            if (providerType === PROVIDER_TYPES.maxBusiness) {
                this.maxBusinessTokenError = ''
                this.maxBusinessToken = ''
                this.$store.commit('setChannelCreationStage', 2)
            } else {
                this.$store.commit('setChannelCreationStage', 3)
            }
        },
        confirmMaxBusinessToken() {
            const token = this.maxBusinessToken?.trim()
            if (!token) {
                this.maxBusinessTokenError = 'Введите токен MAX Business'
                return
            }
            this.maxBusinessTokenError = ''
            const current = this.currentAddingConnector || {}
            this.$store.commit('project/updateCurrentAddingConnector', {
                ...current,
                maxBusinessToken: token,
            })
            this.$store.commit('setChannelCreationStage', 3)
        },
        async createAndAddChannel() {
            if (!this.project) {
                this.$store.dispatch('callNotify', 'Ошибка: проект не найден')
                this.cancel()
                return
            }

            // Блокируем интерфейс на всю цепочку (создание + приоритеты + синхронизация таблицы)
            if ((this.$store.state.channelCreationLockCount || 0) === 0) {
                this.$store.commit('startChannelCreationLock')
            }
            this.isCreating = true
            this.isLoading = true
            const projectId = this.project.id

            try {
                // currentAddingConnector приходит из Stage1 и содержит providerType
                const providerType = this.currentAddingConnector?.providerType || this.currentAddingConnector?.id

                if (!providerType) {
                    this.$store.dispatch('callNotify', 'Ошибка: не указан тип провайдера')
                    this.cancel()
                    return
                }

                // Для всех провайдеров WhatsApp создаем как канал
                const channelType = this.currentAddingConnector?.channelType || PROVIDER_TO_CHANNEL_MAP[providerType]
                if (!channelType) {
                    this.$store.dispatch('callNotify', 'Ошибка: не указан тип канала')
                    this.cancel()
                    return
                }
                const connectorType = channelType

                // Создаём объект коннектора
                const connectorData = {
                    projectId,
                    connectorType: connectorType,
                    domain: null,
                    settings: {},
                    providerType: providerType, // providerType передаем для всех каналов
                }
                const connector = await this.$store.dispatch('project/createConnector', connectorData)

                // Отправляем на бэк и сохраняем в store
                const res = await this.$store.dispatch('project/addConnector', connector)
                if (res.error) {
                    this.$store.dispatch('callNotify', 'Ошибка при создании коннектора')
                    this.cancel()
                    return
                }

                // Получаем обновленный коннектор после создания
                const connectorId = connector.connectorId
                let updatedConnector = connector
                
                if (connectorId) {
                    try {
                        const { data: fetchedConnector, error } = await fetchConnector(connectorId)
                        if (!error && fetchedConnector) {
                            updatedConnector = fetchedConnector
                            this.$store.commit('updateConnector', fetchedConnector)
                        }
                    } catch (e) {
                        console.error('Ошибка при получении данных коннектора:', e)
                    }
                }

                // Сохраняем коннектор в loggedAddingConnector для Stage3
                this.$store.commit('project/setLoggedAddingConnector', updatedConnector)

                // После успешного создания коннектора вызываем createChannel для всех каналов
                if (connectorId) {
                    const channelRes = await createChannel({ connectorId })
                    if (channelRes.error) {
                        console.error('Ошибка при создании канала через networkFixer:', channelRes.error)
                        // Не прерываем процесс, так как коннектор уже создан
                    } else {
                        const slotId = await this.resolveSlotIdForChannelAssignment()
                        if (slotId) {
                            const { error: slotError } = await networkFixerApi.setSlot({
                                slotId,
                                channelConnectorId: connectorId,
                            })
                            if (slotError) {
                                console.error('Ошибка привязки канала к слоту:', slotError)
                                this.$store.dispatch('callNotify', 'Канал создан, но не удалось привязать его к слоту')
                            }
                        }

                        // Обновляем коннектор в store после создания канала, чтобы channelId был доступен
                        try {
                            const { data: fetchedConnector } = await fetchConnector(connectorId)
                            if (fetchedConnector) {
                                this.$store.commit('updateConnector', fetchedConnector)
                                updatedConnector = fetchedConnector
                                // Обновляем также loggedAddingConnector
                                this.$store.commit('project/setLoggedAddingConnector', fetchedConnector)
                                
                                // Автоматически присваиваем последний приоритет во всех колонках
                                if (!this.isCustomCascadesChannelCreation) {
                                    await this.assignLastPriorityToChannel(fetchedConnector, providerType)
                                }
                            }
                        } catch (e) {
                            console.warn('Не удалось обновить коннектор в store после создания канала:', e)
                        }
                    }
                }

                if (
                    providerType === PROVIDER_TYPES.maxBusiness &&
                    this.currentAddingConnector?.maxBusinessToken
                ) {
                    const connectRes = await networkFixerApi.connectMaxBusiness({
                        connectorId,
                        token: this.currentAddingConnector.maxBusinessToken,
                    })
                    if (connectRes?.error || connectRes?.data?.error) {
                        const errText =
                            connectRes?.data?.data ||
                            connectRes?.error ||
                            'Не удалось подключить MAX Business'
                        this.$store.dispatch('callNotify', String(errText))
                    }
                }

                // После создания коннектора переходим на Stage4
                this.$store.commit('setChannelCreationStage', 4)
                this.isLoading = false

                // Дожидаемся, пока таблица каналов синхронизирует/нормализует приоритеты (reload-channel-priorities)
                await this.waitForPrioritiesSync(projectId)
                this.$root.$emit('reload-channel-slots')
            } catch (error) {
                console.error('Ошибка при создании канала:', error)
                const errorMessage = 'Ошибка при создании канала'
                this.$store.dispatch('callNotify', errorMessage)
                this.cancel()
            } finally {
                this.isCreating = false
                this.isLoading = false
                this.$store.commit('endChannelCreationLock')
            }
        },
        onChannelAuthorizedDuringCreation() {
            this.channelWasAuthorizedDuringCreation = true
            if (this.isStarterPackChannelCreation) {
                void this.finishStarterPackChannelCreation()
            }
        },
        async finishStarterPackChannelCreation() {
            const projectId = this.project?.id
            if (projectId) {
                try {
                    await this.$store.dispatch('project/fetchProject', projectId)
                } catch (e) {
                    // ignore
                }
            }
            this.goToProject()
        },
        async goBack() {
            if (this.isChannelCreationLocked) {
                this.$store.dispatch('callNotify', 'Дождитесь завершения создания канала и установки приоритетов')
                return
            }
            if (this.channelCreationStage === 1 && this.activeGroup) {
                this.currentGroup = null
                this.selectedGroupId = null
                this.selectedChannelId = null
                const newUrl = new URL(window.location.href)
                newUrl.searchParams.delete('group')
                window.history.replaceState({}, '', newUrl.toString())
            } else if (this.channelCreationStage === 2) {
                this.maxBusinessToken = ''
                this.maxBusinessTokenError = ''
                this.$store.commit('setChannelCreationStage', 1)
            } else if (this.channelCreationStage === 3) {
                this.resetChannelSelection()
            } else if (this.channelCreationStage === 4) {
                if (this.loggedAddingConnector?.connectorId) {
                    await this.$store.dispatch('project/deleteConnector', this.loggedAddingConnector.connectorId)
                    this.$root.$emit('reload-channel-slots')
                }
                this.$store.commit('project/setLoggedAddingConnector', null)
                this.resetChannelSelection()
            }
        },
        createConnector() {
            // Для WhatsApp (рассылки) - завершаем создание
            this.goToProject()
        },
        goToProject() {
            const wasStarterPackContext = this.$store.state.channelCreationContext === 'starter-pack'
            // Закрываем сайдбар и возвращаемся на главную страницу
            this.$store.commit('resetChannelCreationLock')
            this.$store.commit('setChannelCreationMode', false)
            this.$store.commit('setChannelCreationStage', null)
            this.$store.commit('setChannelCreationContext', null)
            this.$store.commit('setIsConnectorSettingsSidebar', false)
            this.$store.commit('setConnectorSettingsSidebarId', null)
            this.$store.commit('project/setLoggedAddingConnector', null)
            this.$store.commit('project/updateCurrentAddingConnector', null)
            // Очищаем служебные query параметры при закрытии
            if (this.$route?.query?.group || this.$route?.query?.slotId || this.$route?.query?.connectTab) {
                const query = { ...this.$route.query }
                delete query.group
                delete query.slotId
                delete query.connectTab
                this.$router.replace({ query })
            }
            if (wasStarterPackContext) {
                this.$root.$emit('reload-channel-slots')
                this.$root.$emit('starter-pack-cascade-reload')
            }
        },
        /**
         * Автоматически присваивает последний приоритет новому каналу во всех колонках
         */
        async assignLastPriorityToChannel(connector, providerType) {
            if (!connector || !connector.channelId) {
                console.warn('Не удалось присвоить приоритет: отсутствует channelId')
                return
            }

            const channelId = connector.channelId
            const projectId = this.project.id
            const isWhatsappDh = providerType === PROVIDER_TYPES.whatsapp

            try {
                // Получаем все каскады проекта
                const { data: cascades, error } = await getCascadesByProject(projectId)
                if (error) {
                    console.error('Ошибка загрузки каскадов:', error)
                    return
                }

                if (!cascades || cascades.length === 0) {
                    this.$store.dispatch('callNotify', 'На проекте нет каскадов. Приоритет для нового канала не установлен.')
                    return
                }

                // Типы каскадов для обработки.
                // Для большинства non-YC проектов crmMessages-каскад отсутствует — это не ошибка.
                const availableCascadeTypes = new Set((cascades || []).map((c) => c?.cascadeType).filter(Boolean))
                const cascadeTypes = ['mailings', 'notifications', 'crmMessages'].filter((type) =>
                    availableCascadeTypes.has(type),
                )

                for (const cascadeType of cascadeTypes) {
                    // Для WhatsApp DH каналов пропускаем notifications
                    if (cascadeType === 'notifications' && isWhatsappDh) {
                        continue
                    }

                    // Находим каскад нужного типа (создание каскадов с фронта запрещено)
                    const cascade = cascades?.find(c => c && c.cascadeType === cascadeType)
                    if (!cascade) {
                        continue
                    }

                    // Находим максимальный приоритет среди существующих каналов в каскаде
                    const existingChannels = cascade.channels || []
                    const maxPriority = existingChannels.length > 0
                        ? Math.max(...existingChannels.map(ch => ch.priority || 0))
                        : 0

                    // Устанавливаем новый приоритет = max + 1
                    const newPriority = maxPriority + 1

                    // Обновляем приоритет канала в каскаде
                    const { error: updateError } = await updateCascadeChannelPriority({
                        cascadeId: cascade.id,
                        channelId: channelId,
                        priority: newPriority,
                    })

                    if (updateError) {
                        console.error(`Ошибка установки приоритета для ${cascadeType}:`, updateError)
                    } else {
                        console.log(`Приоритет ${newPriority} установлен для канала ${channelId} в каскаде ${cascadeType}`)
                    }
                }
            } catch (error) {
                console.error('Ошибка при присвоении приоритетов каналу:', String(error))
            }
        },
        async waitForPrioritiesSync(projectId) {
            // обновляем проект в store, чтобы коннектор/канал точно был в таблице
            try {
                await this.$store.dispatch('project/fetchProject', projectId)
            } catch (e) {
                // ignore
            }

            if (this.isCustomCascadesChannelCreation) {
                return
            }

            if (this.isStarterPackChannelCreation) {
                this.$root.$emit('starter-pack-cascade-reload')
                return
            }

            // Триггерим обновление приоритетов в таблице
            try {
                this.$root.$emit('reload-channel-priorities')
            } catch (e) {
                // ignore
            }

            // Ждём сигнал, что перезагрузка/нормализация закончилась
            const wait = new Promise((resolve) => {
                this.waitPrioritiesResolver = resolve
            })
            const timeout = new Promise((resolve) => setTimeout(resolve, 30000))
            await Promise.race([wait, timeout])
            this.waitPrioritiesResolver = null
        },
        async cancel() {
            // Отменяем создание и закрываем сайдбар
            if (this.loggedAddingConnector?.connectorId) {
                await this.$store.dispatch('project/deleteConnector', this.loggedAddingConnector.connectorId)
                this.$root.$emit('reload-channel-slots')
            }
            this.goToProject()
        },
        async copyConnectorId() {
            const connectorId = this.connector?.connectorId
            if (!connectorId || this.copyTooltip) {
                return
            }
            try {
                await navigator.clipboard.writeText(connectorId)
                this.copyTooltip = true
                setTimeout(() => {
                    this.copyTooltip = false
                }, 3000)
            } catch (err) {
                console.error('Ошибка при копировании ID:', err)
            }
        },
    },
    mounted() {
        // Обновляем currentGroup из URL при монтировании
        const urlParams = new URLSearchParams(window.location.search)
        this.currentGroup = urlParams.get('group')
        this.connectTab = this.initialConnectTab
        void this.prepareChannelCreationData()
    },
    beforeDestroy() {
        if (this.waitPrioritiesResolver) {
            this.waitPrioritiesResolver()
            this.waitPrioritiesResolver = null
        }
        // Очищаем интервал при уничтожении компонента
        if (this.urlCheckInterval) {
            clearInterval(this.urlCheckInterval)
            this.urlCheckInterval = null
        }
    },
    created() {
        this._prioritiesSyncHandler = () => {
            if (this.waitPrioritiesResolver) {
                this.waitPrioritiesResolver()
            }
        }
        this.$root.$on('channel-create-priorities-sync-done', this._prioritiesSyncHandler)
    },
    destroyed() {
        if (this._prioritiesSyncHandler) {
            this.$root.$off('channel-create-priorities-sync-done', this._prioritiesSyncHandler)
        }
    },
}
</script>

<style lang="sass" scoped>
@import '@/vars.sass'

.add-channel-sidebar-content
    padding: 0
    width: 100%
    max-width: 100%
    min-width: 0
    box-sizing: border-box
    overflow-x: hidden

.stage-content
    min-height: 200px
    width: 100%
    max-width: 100%
    min-width: 0
    box-sizing: border-box

.add-channel-sidebar-loader
    display: flex
    align-items: center
    justify-content: center
    min-height: 200px
    width: 100%

.header-row
    padding: 0 0 16px 0
    border-bottom: 1px solid #E1E8ED
    margin-bottom: 20px !important
    flex-wrap: nowrap !important
    max-width: 100%

.header-row__main
    display: flex
    align-items: center
    flex: 1
    min-width: 0

.close-btn
    flex-shrink: 0
    margin-left: 8px

.connector-id-wrapper
    margin-top: -12px
    margin-bottom: 16px !important
    padding: 0 0 10px 0
    border-bottom: 1px solid #E1E8ED
    max-width: 100%

.connector-id-container
    display: flex
    align-items: center
    gap: 8px
    padding: 6px 12px
    background: #F5F7FA
    border-radius: 6px
    border: 1px solid #E1E8ED
    transition: all 0.2s ease
    max-width: 100%
    min-width: 0

.connector-id-label
    flex-shrink: 0
    font-size: 12px
    font-weight: 500
    color: #2A3B5199
    letter-spacing: 0.2px

.connector-id-value
    flex: 1
    min-width: 0
    font-family: monospace
    font-size: 12px
    color: #2A3B51
    background: transparent
    padding: 0
    padding-left: 5px
    border: none
    letter-spacing: 0.3px
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap

.connector-id-copy-btn
    flex-shrink: 0
    min-width: 24px !important
    width: 24px !important
    height: 24px !important
    margin-left: auto
    transition: all 0.2s ease
    
    &:hover
        transform: scale(1.1)
        background-color: rgba(55, 142, 255, 0.08) !important

h2.dashboard
    font-weight: 600 !important
    font-size: 20px !important
    color: #2A3B51
    margin-bottom: 0 !important
    letter-spacing: -0.3px
    min-width: 0
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap

.back-btn
    flex-shrink: 0
    color: #378EFF !important
    transition: all 0.2s ease
    
    &:hover
        background-color: rgba(55, 142, 255, 0.08) !important
        transform: translateX(-2px)

.channel-create-tabs
    display: flex
    position: relative
    width: 100%
    max-width: 100%
    min-width: 0
    height: 40px
    gap: 8px
    padding: 4px
    background: #F5F7FA
    border-radius: 8px
    box-sizing: border-box

.channel-create-tabs__slider
    position: absolute
    top: 4px
    left: 4px
    width: calc(50% - 4px)
    height: calc(100% - 8px)
    background: #FFFFFF
    border-radius: 6px
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1)
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)
    z-index: 0

.channel-create-tabs__item
    position: relative
    flex: 1
    min-width: 0
    padding: 8px 12px
    border: none
    border-radius: 6px
    background: transparent
    font-size: 14px
    font-weight: 500
    color: #2A3B51
    cursor: pointer
    transition: color 0.3s ease
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis
    z-index: 1

    &:hover
        color: #378EFF

    &--active
        color: #378EFF
        font-weight: 600

.channel-type-list
    display: flex
    flex-direction: column
    gap: 8px
    width: 100%
    max-width: 100%
    min-width: 0

.channel-type-item
    display: flex
    align-items: center
    gap: 12px
    padding: 12px 16px
    border-radius: 8px
    border: 1px solid #E1E8ED
    cursor: pointer
    transition: all 0.15s ease
    overflow: hidden
    height: 60px
    width: 100%
    max-width: 100%
    min-width: 0
    box-sizing: border-box

    &:hover
        border-color: #378EFF
        background: rgba(55, 142, 255, 0.04)

    &--selected
        border-color: #378EFF
        background: rgba(55, 142, 255, 0.06)

    &--transfer
        position: relative
        align-items: center
        min-height: 60px
        height: auto
        padding: 12px 16px 12px 34px
        gap: 12px

.channel-type-item__name
    font-size: 15px
    font-weight: 500
    color: #2A3B51
    min-width: 0
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap

.channel-transfer-item__text
    display: flex
    flex-direction: column
    flex: 1
    min-width: 0

.channel-transfer-item__head
    display: flex
    align-items: center
    min-width: 0

.channel-transfer-item__section
    position: absolute
    top: 0
    bottom: 0
    left: 0
    width: 22px
    display: flex
    align-items: center
    justify-content: center
    writing-mode: vertical-rl
    transform: rotate(180deg)
    letter-spacing: 0.24px
    font-size: 12px
    font-weight: 700
    line-height: 16px
    color: #fff
    padding: 8px 1px
    white-space: nowrap
    flex-shrink: 0

.channel-transfer-item__section--tariff
    background-color: #378EFF

.channel-transfer-item__section--additional
    background: linear-gradient(rgba(255,255,255,0.2), rgba(255,255,255,0.2)), #2A3B51

.channel-transfer-item__sub
    font-weight: 400
    font-size: 12px
    line-height: 1.3
    color: rgba(42,59,81,0.87)
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap

.channel-transfer-placeholder
    min-height: 140px
    display: flex
    align-items: center
    justify-content: center
    padding: 24px 16px
    border: 1px dashed #D7E0EA
    border-radius: 10px
    color: #2A3B5199
    font-size: 14px
    text-align: center
    box-sizing: border-box
    width: 100%
    max-width: 100%

.channel-transfer-warning
    padding: 10px 12px
    background: rgba(55, 142, 255, 0.06)
    border-radius: 4px
    box-sizing: border-box
    width: 100%
    max-width: 100%
    overflow-wrap: anywhere
    word-break: break-word

.channel-transfer-warning__title
    font-size: 14px
    font-weight: 700
    line-height: 18px
    color: #2A3B51
    margin-bottom: 4px

.channel-transfer-warning__text
    font-size: 14px
    line-height: 20px
    color: rgba(42, 59, 81, 0.87)

.slot-freeze-banner
    width: 100%
    max-width: 100%
    min-height: 40px
    height: auto
    display: flex
    align-items: center
    justify-content: space-between
    gap: 8px
    padding: 8px 10px
    border: 1px solid #FD9C2B
    border-radius: 8px
    background-image: linear-gradient(90deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.9) 100%), linear-gradient(90deg, #FD9C2B 0%, #FD9C2B 100%)
    box-sizing: border-box

.slot-freeze-banner--paid
    border-color: #378EFF
    background-image: linear-gradient(90deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.9) 100%), linear-gradient(90deg, #378EFF 0%, #378EFF 100%)

.slot-freeze-banner__left
    display: flex
    align-items: center
    flex: 1 1 auto
    min-width: 0
    overflow: hidden

.slot-freeze-banner__text
    display: block
    min-width: 0
    max-width: 100%
    font-size: 14px
    line-height: 18px
    color: #2A3B51
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap

.slot-freeze-banner__actions
    display: flex
    align-items: center
    gap: 8px
    flex-shrink: 0

.slot-freeze-banner__auto-renewal
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    padding: 4px 8px 2px
    border-radius: 4px
    white-space: nowrap
    cursor: pointer

.slot-freeze-banner__auto-renewal-title
    margin-bottom: -2px
    color: #378EFF
    font-size: 10px
    font-weight: 500
    line-height: normal
    letter-spacing: 0.6px
    text-transform: uppercase

.slot-freeze-banner__auto-renewal-date
    color: #7F8997
    cursor: pointer
    font-size: 12px
    font-weight: 400
    line-height: 16px
    text-align: center

.slot-freeze-banner__btn
    min-width: auto !important
    height: 28px !important
    padding: 0 8px !important
    border-radius: 4px !important
    font-size: 10px !important
    font-weight: 500 !important
    letter-spacing: 0.6px !important
    text-transform: uppercase
    box-shadow: none !important

.slot-freeze-banner__menu-btn
    width: 28px !important
    min-width: 28px !important
    height: 28px !important
    padding: 0 !important

.warning-text-wrapper
    background: #FFF3E0 0% 0% no-repeat padding-box
    border: 1px solid #FFE0B2
    border-radius: 8px
    padding: 12px 16px
    overflow: hidden
    width: 100%
    max-width: 100%
    box-sizing: border-box
    overflow-wrap: anywhere
    word-break: break-word

.description-text-wrapper__warning
    font: normal normal 600 14px/20px Geologica
    letter-spacing: 0px
    color: #F57C00

.description-text-wrapper__text
    font: normal normal normal 14px/16px Geologica
    letter-spacing: 0px
    color: #2A3B51DE
    margin-bottom: 0

// ChannelSettingsForm внутри сайдбара: фиксированные ширины инпутов не должны вылезать за viewport
::v-deep .channel-settings-form
    width: 100%
    max-width: 100%
    min-width: 0
    box-sizing: border-box

::v-deep .right-panel,
::v-deep .right-panel-content
    width: 100%
    max-width: 100%
    min-width: 0

::v-deep .timezone-picker .v-input__slot,
::v-deep .custom-input .v-input__slot,
::v-deep .messenger-input .v-input__slot,
::v-deep .phone-group-input .v-input__slot
    width: 100% !important
    max-width: 100%

::v-deep .phone-input .v-input__slot
    width: 100% !important
    max-width: 174px

::v-deep .qr-code-wrapper,
::v-deep .qr-code-loading-wrapper
    width: min(256px, 100%)
    height: auto
    aspect-ratio: 1 / 1
    max-width: 100%

@media (max-width: 480px)
    h2.dashboard
        font-size: 18px !important

    .channel-create-tabs__item
        padding: 8px 4px
        font-size: 12px

    .channel-type-item
        gap: 8px
        padding: 12px

    .channel-type-item--transfer
        padding: 12px 12px 12px 34px

    ::v-deep .test-message .d-flex.align-center
        flex-wrap: wrap
        gap: 8px

    ::v-deep .phone-input
        margin-right: 0 !important
        width: 100%

    ::v-deep .phone-input .v-input__slot
        max-width: 100%

    ::v-deep .test-message-btn
        width: 100%
</style>

